import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { KeycloakAuthService } from './core/auth/keycloak-auth.service';
import { I18nService, Language } from './core/i18n/i18n.service';
import { TranslatePipe } from './core/i18n/translate.pipe';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent {
  private readonly auth = inject(KeycloakAuthService);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly themeStorageKey = 'configurator-theme';

  authenticated = false;
  tenantId = '';
  error = '';
  theme: 'light' | 'dark' = 'light';

  constructor() {
    this.initTheme();
    this.syncAuthState();
    const isCallback = this.auth.isRedirectCallback();
    const authFlow = isCallback ? this.auth.handleRedirectCallback() : Promise.resolve();

    void authFlow
      .then(() => this.auth.ensureSession())
      .then(() => {
        this.syncAuthState();
      })
      .catch((err: unknown) => {
        this.error = err instanceof Error ? err.message : this.i18n.t('auth.callbackFailed');
        this.syncAuthState();
      });
  }

  get language(): Language {
    return this.i18n.language;
  }

  setLanguage(language: string): void {
    if (language === 'de' || language === 'en') {
      this.i18n.setLanguage(language);
    }
  }

  get isUserConfigPage(): boolean {
    return this.router.url.startsWith('/user/configure');
  }

  login(): void {
    void this.auth.login();
  }

  logout(): void {
    this.auth.logout();
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    this.applyTheme();
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.themeStorageKey, this.theme);
    }
  }

  private syncAuthState(): void {
    this.authenticated = this.auth.isAuthenticated();
    this.tenantId = this.auth.tenantId();
  }

  private initTheme(): void {
    let resolvedTheme: 'light' | 'dark' = 'light';
    if (typeof localStorage !== 'undefined') {
      const storedTheme = localStorage.getItem(this.themeStorageKey);
      if (storedTheme === 'light' || storedTheme === 'dark') {
        resolvedTheme = storedTheme;
      }
    }
    this.theme = resolvedTheme;
    this.applyTheme();
  }

  private applyTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.classList.toggle('theme-dark', this.theme === 'dark');
  }
}
